// src/pages/MessagesPage.tsx
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Paper, TextField, IconButton, Avatar,
  List, ListItem, ListItemButton, ListItemAvatar, ListItemText, Divider, Badge,
  CircularProgress, Alert, useTheme, alpha, Grid, Tabs, Tab, Button, Chip
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import {
  getConversations,
  getConversation,
  sendMessage,
  getMessageRequests,
  acceptMessageRequest,
  rejectMessageRequest,
  type Conversation,
  type Message,
  type MessageRequest,
  type MessageLimits
} from '../services/message';
import {
  getMySupportConversation,
  sendSupportMessage as sendSupportMsg,
  getUnreadSupportCount
} from '../services/support';
import { useAuth } from '../context/AuthContext';

const MessagesPage = () => {
  const { userId: selectedUserId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user: currentUser } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messageRequests, setMessageRequests] = useState<MessageRequest[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [messageRequestStatus, setMessageRequestStatus] = useState<{
    isMessageRequest: boolean;
    requestAccepted: boolean | null;
    isSender: boolean;
  } | null>(null);
  const [messageLimits, setMessageLimits] = useState<MessageLimits | null>(null);
  const [supportUnreadCount, setSupportUnreadCount] = useState<number>(0);

  // Load conversations list and message requests
  useEffect(() => {
    loadConversations();
    loadMessageRequests();
    loadSupportUnreadCount();
  }, []);

  // Load messages when user is selected
  useEffect(() => {
    if (selectedUserId) {
      loadMessages(selectedUserId);
      // Find and set the selected conversation
      const conv = conversations.find(c => c.user.id === selectedUserId);
      setSelectedConversation(conv || null);
    }
  }, [selectedUserId, conversations]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadConversations = async () => {
    try {
      const data = await getConversations();
      setConversations(data);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessageRequests = async () => {
    try {
      const data = await getMessageRequests();
      setMessageRequests(data);
    } catch (error) {
      console.error('Error loading message requests:', error);
    }
  };

  const loadSupportUnreadCount = async () => {
    try {
      const count = await getUnreadSupportCount();
      setSupportUnreadCount(count);
    } catch (error) {
      console.error('Error loading support unread count:', error);
    }
  };

  const loadMessages = async (userId: string) => {
    try {
      // Check if this is support conversation
      if (userId === 'support') {
        const supportMessages = await getMySupportConversation();
        // Transform support messages to regular message format
        setMessages(supportMessages.map(msg => ({
          id: msg.id,
          senderId: msg.isFromAdmin ? 'support' : (currentUser?.id || ''),
          receiverId: msg.isFromAdmin ? (currentUser?.id || '') : 'support',
          content: msg.message,
          isRead: msg.isRead,
          readAt: msg.readAt,
          createdAt: msg.createdAt
        })));
        setMessageRequestStatus(null);
        setMessageLimits(null);
        // Reset unread count
        setSupportUnreadCount(0);
      } else {
        const response = await getConversation(userId);
        setMessages(response.messages);
        setMessageRequestStatus(response.messageRequestStatus);
        setMessageLimits(response.limitInfo || null);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      setMessageRequestStatus(null);
      setMessageLimits(null);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedUserId || sendingMessage) return;

    setSendingMessage(true);
    try {
      if (selectedUserId === 'support') {
        // Send support message
        const supportMessage = await sendSupportMsg(messageText.trim());
        setMessages([...messages, {
          id: supportMessage.id,
          senderId: currentUser?.id || '',
          receiverId: 'support',
          content: supportMessage.message,
          isRead: supportMessage.isRead,
          readAt: supportMessage.readAt,
          createdAt: supportMessage.createdAt
        }]);
        setMessageText('');
      } else {
        // Send regular message
        const newMessage = await sendMessage(selectedUserId, messageText.trim());
        setMessages([...messages, newMessage]);
        setMessageText('');
        // Refresh conversations to update last message
        await loadConversations();
        // Reload messages to get updated limit info
        await loadMessages(selectedUserId);
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      const errorMessage = typeof error.response?.data?.error === 'string'
        ? error.response?.data?.error
        : error.response?.data?.error?.message || 'Failed to send message';
      alert(errorMessage);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSelectConversation = (userId: string) => {
    navigate(`/messages/${userId}`);
  };

  const handleAcceptRequest = async (userId: string) => {
    try {
      await acceptMessageRequest(userId);
      // Reload both lists
      await loadMessageRequests();
      await loadConversations();
      // Navigate to conversation
      navigate(`/messages/${userId}`);
    } catch (error) {
      console.error('Error accepting message request:', error);
      alert('Failed to accept message request');
    }
  };

  const handleRejectRequest = async (userId: string) => {
    try {
      await rejectMessageRequest(userId);
      // Reload message requests
      await loadMessageRequests();
    } catch (error) {
      console.error('Error rejecting message request:', error);
      alert('Failed to reject message request');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
        Messages
      </Typography>

      <Paper
        elevation={3}
        sx={{
          height: 'calc(100vh - 200px)',
          display: 'flex',
          borderRadius: 4,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 1)})`,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >
        {/* Conversations List */}
        <Box
          sx={{
            width: { xs: '100%', md: 360 },
            borderRight: { md: `1px solid ${alpha(theme.palette.divider, 0.1)}` },
            display: { xs: selectedUserId ? 'none' : 'block', md: 'block' },
            overflowY: 'auto',
            background: alpha(theme.palette.background.default, 0.3),
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: alpha(theme.palette.divider, 0.05),
            },
            '&::-webkit-scrollbar-thumb': {
              background: alpha(theme.palette.primary.main, 0.3),
              borderRadius: '4px',
              '&:hover': {
                background: alpha(theme.palette.primary.main, 0.5),
              },
            },
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
              <Tab label="Conversations" />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Requests
                    {messageRequests.length > 0 && (
                      <Chip
                        label={messageRequests.length}
                        size="small"
                        color="error"
                        sx={{ height: 20, minWidth: 20 }}
                      />
                    )}
                  </Box>
                }
              />
            </Tabs>
          </Box>

          {/* Conversations Tab */}
          {currentTab === 0 && (
            <>
              <List sx={{ p: 1 }}>
                {/* VCG Support - Always at top */}
                <ListItemButton
                  selected={selectedUserId === 'support'}
                  onClick={() => handleSelectConversation('support')}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    transition: 'all 0.2s ease-in-out',
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      transform: 'translateX(4px)',
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    },
                    '&.Mui-selected': {
                      bgcolor: alpha(theme.palette.primary.main, 0.15),
                      borderLeft: `3px solid ${theme.palette.primary.main}`,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                      }
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Badge
                      badgeContent={supportUnreadCount}
                      color="error"
                      overlap="circular"
                      sx={{
                        '& .MuiBadge-badge': {
                          animation: supportUnreadCount > 0 ? 'pulse 2s infinite' : 'none',
                          '@keyframes pulse': {
                            '0%': { transform: 'scale(1)' },
                            '50%': { transform: 'scale(1.1)' },
                            '100%': { transform: 'scale(1)' },
                          }
                        }
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: theme.palette.primary.main,
                          border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                        }}
                      >
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                          VCG
                        </Typography>
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                        VCG Support
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        Get help from our team
                      </Typography>
                    }
                  />
                </ListItemButton>

                {/* Regular conversations */}
                {conversations.length > 0 && conversations.map((conversation) => (
                    <ListItemButton
                      key={conversation.user.id}
                      selected={selectedUserId === conversation.user.id}
                      onClick={() => handleSelectConversation(conversation.user.id)}
                      sx={{
                        borderRadius: 2,
                        mb: 0.5,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateX(4px)',
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                        },
                        '&.Mui-selected': {
                          bgcolor: alpha(theme.palette.primary.main, 0.15),
                          borderLeft: `3px solid ${theme.palette.primary.main}`,
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                          }
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Badge
                          badgeContent={conversation.unreadCount}
                          color="error"
                          overlap="circular"
                          sx={{
                            '& .MuiBadge-badge': {
                              animation: conversation.unreadCount > 0 ? 'pulse 2s infinite' : 'none',
                              '@keyframes pulse': {
                                '0%': { transform: 'scale(1)' },
                                '50%': { transform: 'scale(1.1)' },
                                '100%': { transform: 'scale(1)' },
                              }
                            }
                          }}
                        >
                          <Avatar
                            src={conversation.user.photoUrl || ''}
                            alt={conversation.user.firstName}
                            sx={{
                              width: 48,
                              height: 48,
                              border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                            }}
                          />
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: conversation.unreadCount > 0 ? 700 : 500,
                              color: conversation.unreadCount > 0 ? theme.palette.text.primary : theme.palette.text.secondary
                            }}
                          >
                            {`${conversation.user.firstName} ${conversation.user.lastName}`}
                          </Typography>
                        }
                        secondary={
                          conversation.lastMessage ? (
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontWeight: conversation.unreadCount > 0 ? 600 : 400,
                                color: conversation.unreadCount > 0 ? theme.palette.text.primary : theme.palette.text.secondary
                              }}
                            >
                              {conversation.lastMessage.isSentByMe ? '✓ ' : ''}
                              {conversation.lastMessage.content}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              No messages yet
                            </Typography>
                          )
                        }
                      />
                    </ListItemButton>
                ))}
              </List>
            </>
          )}

          {/* Message Requests Tab */}
          {currentTab === 1 && (
            <>
              {messageRequests.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <Alert severity="info">
                    No pending message requests
                  </Alert>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {messageRequests.map((request) => (
                    <ListItem
                      key={request.user.id}
                      sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                        <Avatar src={request.user.photoUrl || ''} alt={request.user.firstName} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2">
                            {request.user.firstName} {request.user.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {request.user.title}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          pl: 7,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          width: '100%'
                        }}
                      >
                        {request.message.content}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, pl: 7, width: '100%' }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<CheckIcon />}
                          onClick={() => handleAcceptRequest(request.user.id)}
                        >
                          Accept
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<CloseIcon />}
                          onClick={() => handleRejectRequest(request.user.id)}
                        >
                          Reject
                        </Button>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}
        </Box>

        {/* Chat Area */}
        <Box
          sx={{
            flex: 1,
            display: { xs: selectedUserId ? 'flex' : 'none', md: 'flex' },
            flexDirection: 'column'
          }}
        >
          {!selectedUserId ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Select a conversation to start messaging
              </Typography>
            </Box>
          ) : (
            <>
              {/* Chat Header */}
              <Box
                sx={{
                  p: 2.5,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.background.paper, 0.8)})`,
                  backdropFilter: 'blur(10px)',
                }}
              >
                <IconButton
                  sx={{
                    display: { md: 'none' },
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    }
                  }}
                  onClick={() => navigate('/messages')}
                >
                  <ArrowBackIcon />
                </IconButton>
                {selectedUserId === 'support' ? (
                  <>
                    <Avatar
                      sx={{
                        width: 50,
                        height: 50,
                        bgcolor: theme.palette.primary.main,
                        border: `3px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                      }}
                    >
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                        VCG
                      </Typography>
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.25, color: theme.palette.primary.main }}>
                        VCG Support
                      </Typography>
                      <Typography variant="caption" sx={{ color: alpha(theme.palette.text.secondary, 0.8) }}>
                        Get help from our team
                      </Typography>
                    </Box>
                  </>
                ) : selectedConversation && (
                  <>
                    <Avatar
                      src={selectedConversation.user.photoUrl || ''}
                      alt={selectedConversation.user.firstName}
                      sx={{
                        width: 50,
                        height: 50,
                        border: `3px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                      }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.25 }}>
                        {selectedConversation.user.firstName} {selectedConversation.user.lastName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: alpha(theme.palette.text.secondary, 0.8) }}>
                        {selectedConversation.user.title}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>

              {/* Message Request Status Banner */}
              {messageRequestStatus && messageRequestStatus.isMessageRequest && (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: messageRequestStatus.requestAccepted === null
                      ? alpha(theme.palette.warning.main, 0.1)
                      : messageRequestStatus.requestAccepted === false
                      ? alpha(theme.palette.error.main, 0.1)
                      : 'transparent',
                    borderBottom: 1,
                    borderColor: 'divider'
                  }}
                >
                  {messageRequestStatus.requestAccepted === null && (
                    <>
                      {messageRequestStatus.isSender ? (
                        <Alert severity="info">
                          Your message request is pending. Waiting for {selectedConversation?.user.firstName} to accept.
                        </Alert>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Alert severity="warning" sx={{ flex: 1 }}>
                            {selectedConversation?.user.firstName} sent you a message request
                          </Alert>
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<CheckIcon />}
                            onClick={() => selectedUserId && handleAcceptRequest(selectedUserId)}
                          >
                            Accept
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<CloseIcon />}
                            onClick={() => selectedUserId && handleRejectRequest(selectedUserId)}
                          >
                            Reject
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                  {messageRequestStatus.requestAccepted === false && (
                    <Alert severity="error">
                      This message request was rejected
                    </Alert>
                  )}
                </Box>
              )}

              {/* Mentorship Message Limits Banner */}
              {messageLimits && (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: messageLimits.remaining < 10
                      ? alpha(theme.palette.error.main, 0.1)
                      : messageLimits.remaining < 20
                      ? alpha(theme.palette.warning.main, 0.1)
                      : alpha(theme.palette.info.main, 0.1),
                    borderBottom: 1,
                    borderColor: 'divider'
                  }}
                >
                  <Alert
                    severity={
                      messageLimits.remaining < 10
                        ? 'error'
                        : messageLimits.remaining < 20
                        ? 'warning'
                        : 'info'
                    }
                    sx={{ alignItems: 'center' }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                      <Typography variant="body2">
                        <strong>Mentorship Messages:</strong> {messageLimits.currentCount} / {messageLimits.maxCount} used this month
                      </Typography>
                      <Chip
                        label={`${messageLimits.remaining} remaining`}
                        size="small"
                        color={
                          messageLimits.remaining < 10
                            ? 'error'
                            : messageLimits.remaining < 20
                            ? 'warning'
                            : 'info'
                        }
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                    {messageLimits.remaining < 10 && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        Upgrade to Pro for 500 messages per expert (50 SAR/month)
                      </Typography>
                    )}
                  </Alert>
                </Box>
              )}

              {/* Messages Area */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  p: 3,
                  background: `radial-gradient(ellipse at top, ${alpha(theme.palette.primary.main, 0.03)}, transparent 70%),
                              radial-gradient(ellipse at bottom, ${alpha(theme.palette.secondary.main, 0.02)}, transparent 70%)`,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: alpha(theme.palette.divider, 0.05),
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: alpha(theme.palette.primary.main, 0.3),
                    borderRadius: '4px',
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.5),
                    },
                  },
                }}
              >
                {messages.length === 0 ? (
                  <Box sx={{ textAlign: 'center', mt: 8 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No messages yet
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                      Start the conversation!
                    </Typography>
                  </Box>
                ) : (
                  messages.map((message, index) => {
                    const isSentByMe = message.senderId === currentUser?.id;
                    const showDate = index === 0 ||
                      new Date(messages[index - 1].createdAt).toDateString() !== new Date(message.createdAt).toDateString();

                    return (
                      <Box key={message.id}>
                        {showDate && (
                          <Box sx={{ textAlign: 'center', my: 2 }}>
                            <Chip
                              label={new Date(message.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                              size="small"
                              sx={{
                                bgcolor: alpha(theme.palette.background.paper, 0.8),
                                backdropFilter: 'blur(10px)',
                                fontSize: '0.75rem'
                              }}
                            />
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: isSentByMe ? 'flex-end' : 'flex-start',
                            mb: 1.5,
                            animation: 'slideIn 0.3s ease-out',
                            '@keyframes slideIn': {
                              from: {
                                opacity: 0,
                                transform: isSentByMe ? 'translateX(20px)' : 'translateX(-20px)'
                              },
                              to: {
                                opacity: 1,
                                transform: 'translateX(0)'
                              }
                            }
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: '70%',
                              position: 'relative'
                            }}
                          >
                            <Box
                              sx={{
                                bgcolor: isSentByMe
                                  ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                                  : alpha(theme.palette.background.paper, 0.95),
                                background: isSentByMe
                                  ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                                  : alpha(theme.palette.background.paper, 0.95),
                                color: isSentByMe ? 'white' : 'text.primary',
                                p: 1.5,
                                px: 2,
                                borderRadius: 3,
                                borderTopRightRadius: isSentByMe ? 4 : 16,
                                borderTopLeftRadius: isSentByMe ? 16 : 4,
                                borderBottomRightRadius: isSentByMe ? 4 : 16,
                                borderBottomLeftRadius: isSentByMe ? 16 : 4,
                                boxShadow: isSentByMe
                                  ? `0 2px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                                  : `0 2px 12px ${alpha(theme.palette.grey[500], 0.15)}`,
                                border: isSentByMe ? 'none' : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                backdropFilter: 'blur(10px)',
                                transition: 'transform 0.2s ease-in-out',
                                '&:hover': {
                                  transform: 'scale(1.02)'
                                }
                              }}
                            >
                              <Typography variant="body1" sx={{ wordBreak: 'break-word', lineHeight: 1.5 }}>
                                {message.content}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  mt: 0.5,
                                  opacity: 0.7,
                                  fontSize: '0.7rem',
                                  textAlign: 'right'
                                }}
                              >
                                {new Date(message.createdAt).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* Message Input */}
              <Box
                sx={{
                  p: 2.5,
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  background: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.paper, 1)})`,
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
                  <TextField
                    fullWidth
                    placeholder="Type a message..."
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={sendingMessage}
                    multiline
                    maxRows={4}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.background.default, 0.5),
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: alpha(theme.palette.divider, 0.2),
                          borderWidth: 1.5,
                        },
                        '&:hover fieldset': {
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                        },
                        '&.Mui-focused': {
                          bgcolor: alpha(theme.palette.background.paper, 0.95),
                          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                          '& fieldset': {
                            borderColor: theme.palette.primary.main,
                            borderWidth: 2,
                          }
                        }
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.95rem',
                        lineHeight: 1.6,
                      }
                    }}
                  />
                  <IconButton
                    onClick={handleSendMessage}
                    disabled={!messageText.trim() || sendingMessage}
                    sx={{
                      width: 48,
                      height: 48,
                      background: !messageText.trim() || sendingMessage
                        ? alpha(theme.palette.grey[400], 0.3)
                        : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      color: 'white',
                      boxShadow: !messageText.trim() || sendingMessage
                        ? 'none'
                        : `0 4px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: !messageText.trim() || sendingMessage ? 'none' : 'translateY(-2px) scale(1.05)',
                        boxShadow: !messageText.trim() || sendingMessage
                          ? 'none'
                          : `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                        background: !messageText.trim() || sendingMessage
                          ? alpha(theme.palette.grey[400], 0.3)
                          : `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      },
                      '&:active': {
                        transform: !messageText.trim() || sendingMessage ? 'none' : 'scale(0.95)',
                      },
                      '&:disabled': {
                        color: alpha(theme.palette.grey[500], 0.5)
                      }
                    }}
                  >
                    {sendingMessage ? (
                      <CircularProgress size={24} sx={{ color: 'white' }} />
                    ) : (
                      <SendIcon />
                    )}
                  </IconButton>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default MessagesPage;
